/**
 * LeftSidebar — shared style objects for the Synth VR editor's left panel.
 *
 * Colors now use the same oklch token system as Flow/Console so the sidebar
 * re-tints when the active accent / tint theme changes.
 */

import React from 'react';
import { color, font } from '../ui/theme';
import {
  cameraGridStyle,
  cameraBtnStyle,
  cameraActionBtnStyle as saveViewBtnShared,
  uniformBtn,
  clearBtnVariant,
  mapBtnVariant,
} from '../ui/editorSharedStyles';

// Re-export shared camera styles so LeftSidebar.tsx keeps its existing imports.
export { cameraGridStyle, cameraBtnStyle };

export const sidebarStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  // Match Flow's left aside: gap 8 + asymmetric H padding, no vertical padding.
  // The editor header lives OUTSIDE this element; this is the inner scroller, so
  // it fills the remaining height and scrolls. The edge fade is applied
  // dynamically by <ScrollFade>. Native scrollbar hidden (.overlay-scroll).
  gap: 8,
  padding: '0 10px 0 11px',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
};

export const saveViewBtnStyle: React.CSSProperties = {
  ...saveViewBtnShared,
  marginTop: 4,
};

export const transportRow: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: 6,
};

export const transportBtnStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 36,
  minWidth: 0,
  height: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)',
  borderRadius: 12,
  cursor: 'pointer',
  fontSize: 14,
  fontFamily: font.sans,
  transition: 'all 0.15s ease',
  boxSizing: 'border-box',
};

export const bottomBtnsContainer: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

export const bottomBtnRow: React.CSSProperties = {
  display: 'flex',
  gap: 4,
};

// uniformBtnStyle matches Flow's uniformBtn exactly.
export const uniformBtnStyle: React.CSSProperties = { ...uniformBtn };

// Button variants — use the same token-based colors as Flow/Console.
export { clearBtnVariant };

export const newMapBtnVariant: React.CSSProperties = { ...mapBtnVariant };
export const loadMapBtnVariant: React.CSSProperties = { ...mapBtnVariant };
export const saveBtnVariant: React.CSSProperties = { ...mapBtnVariant };
export const backBtnVariant: React.CSSProperties = { ...mapBtnVariant, flex: 1 };

// ── Clear-Map Popup ───────────────────────────────────────────────────────────

export const popupOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'oklch(0 0 0 / 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
};

export const popupWindowStyle: React.CSSProperties = {
  background: 'var(--surface-panel)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.25)',
  borderRadius: 18,
  boxShadow: '0 12px 48px rgba(0,0,0,0.6), 0 0 30px oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.10)',
  overflow: 'hidden',
  userSelect: 'none',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  minWidth: 360,
  maxWidth: 420,
  display: 'flex',
  flexDirection: 'column',
};

export const popupTitleBarStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 16px',
  borderBottom: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.12)',
  flexShrink: 0,
  background: 'oklch(0.065 calc(0.016 * var(--tint-c)) var(--tint-h) / 0.8)',
};

export const popupTitleTextStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: color.ink,
  fontFamily: font.sans,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  letterSpacing: 0.5,
};

export const popupCloseBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.55)',
  color: 'var(--accent)',
  borderRadius: '50%',
  width: 28,
  height: 28,
  cursor: 'pointer',
  fontSize: 13,
  fontFamily: font.sans,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.15s ease',
};

export const popupBodyStyle: React.CSSProperties = {
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

export const popupOptionBtnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  padding: '16px 12px',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.6)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.20)',
  borderRadius: 14,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontFamily: font.sans,
};

export const popupOptionIconStyle: React.CSSProperties = {
  fontSize: 24,
};

export const popupOptionLabelStyle: React.CSSProperties = {
  color: color.ink,
  fontSize: 14,
  fontWeight: 600,
};

export const popupOptionDescStyle: React.CSSProperties = {
  color: color.inkFaint,
  fontSize: 10,
  textAlign: 'center',
};

export const popupCancelBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)',
  color: color.inkFaint,
  borderRadius: 50,
  padding: '10px 16px',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: font.sans,
  width: '100%',
  transition: 'all 0.15s ease',
  marginTop: 4,
};
